import { ChevronDownIcon, LockClosedIcon } from '@heroicons/react/20/solid'
import { cellChevronClasses, editableCellTrigger } from 'app/components/table/cellInteraction'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'app/components/ui/tooltip'
import { cn } from 'app/lib/utils'
import type { TreatmentDestination } from 'app/types/treatment'
import { TREATMENT_STAGES, TreatmentRow } from './useTreatmentBoard'

function StageDot({ dot }: { dot: string }) {
  return <span className={cn(`mr-1.5 inline-block h-2 w-2 shrink-0 rounded-[2px] align-middle`, dot)} />
}

/**
 * The table's answer to dragging a card between columns.
 *
 * Every destination a drag could reach is in one list, exits included, and
 * picking one writes the same `system:treatment` membership the board writes.
 * Follows the shared cell grammar: data at rest, tint and chevron on cell
 * hover, dropdown on click.
 */
export function TreatmentStageCell({
  row,
  onSelect,
  stages = TREATMENT_STAGES,
  locked,
}: {
  row: TreatmentRow
  onSelect: (stage: string) => void
  stages?: TreatmentDestination[]
  /** The booked stage: a row sitting in it shows the stage but offers no choices. */
  locked?: TreatmentDestination
}) {
  if (locked && row.stage === locked.name) {
    // The lock sits where the chevron would, so the cell says why it opens nothing.
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex h-full w-full items-center overflow-hidden px-2">
              <StageDot dot={locked.dot ?? `bg-surface-strong`} />
              <span className="truncate">{locked.name}</span>
              <LockClosedIcon aria-label="Locked" className={cellChevronClasses} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">Set by the booking</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  // The list-backed board has no destinations until its list loads; the row's own stage stands in.
  const current = stages.find((stage) => stage.name === row.stage) ?? stages[0]
  return (
    // modal={false} + stopPropagation: the row's own onRowClick opens the case,
    // so every click that belongs to this cell has to stop before it.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className={editableCellTrigger} onClick={(event) => event.stopPropagation()}>
        <StageDot dot={current?.dot ?? `bg-surface-strong`} />
        <span className="truncate">{current?.name ?? row.stage}</span>
        <ChevronDownIcon className={cellChevronClasses} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 bg-surface-raised"
        onClick={(event) => event.stopPropagation()}
      >
        {stages.map((stage) => (
          <DropdownMenuItem
            key={stage.name}
            title={stage.blurb}
            onSelect={() => stage.name !== current?.name && onSelect(stage.name)}
            className={cn(
              `flex items-center gap-2 text-body-xs`,
              stage.name === current?.name ? `font-semibold text-strong` : `text-default`,
            )}
          >
            <StageDot dot={stage.dot ?? `bg-surface-strong`} />
            <span className="truncate">{stage.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
